import type { Schemas } from "@neio/contracts";
import type { ApprovedListStatus } from "../components/MarketResults";
import { uc5, type LtbDecision, type UC5Job } from "../lib/api";
import { ApiError, apiGet, apiSend, switchedOff } from "../lib/http";

/**
 * The Vendor page's stage transitions, kept out of the component so they can be exercised against
 * a faked `fetch` without a DOM. The page calls these and only copies the result into its state.
 */

type Overrides = { name: string; value: string }[];

/** Jobs whose check is in flight. The research loop is a paid call, so a second click is a no-op. */
const running = new Set<string>();

/**
 * Runs Stage 2. On a refusal or a server error the job comes back unchanged, so the stage holds.
 * Returns null when a check for this job is already running.
 */
export async function checkObsolescence(job: UC5Job): Promise<{ job: UC5Job; error: string | null } | null> {
  if (running.has(job.id)) return null;
  running.add(job.id);
  try {
    return { job: await uc5.obsolescence(job.id), error: null };
  } catch (e) {
    return { job, error: (e as Error).message };
  } finally {
    running.delete(job.id);
  }
}

/** Jobs this page has already checked on opening, so browsing the list does not repeat the paid call. */
const openedOnce = new Set<string>();

/**
 * Opening an approved job with no result opens Stage 2, which runs the check (bug:D10). Only the
 * first opening in a page session does: after a failure the analyst retries with the button.
 * A job whose market search already ran is left alone, because the check route records the job
 * as back at Stage 2 and that would hide its candidates.
 */
export function checkOnOpen(job: UC5Job): boolean {
  if (!job.spec_approved_by || job.obsolescence || job.stage === "market") return false;
  if (openedOnce.has(job.id)) return false;
  openedOnce.add(job.id);
  return true;
}

export interface ApproveOutcome {
  job: UC5Job;
  /** The approval itself failed. Stage 2 has not opened. */
  error: string | null;
  /** The approval succeeded and opened Stage 2, but the check was refused or failed. */
  stage2Error: string | null;
}

/**
 * Approving the specification is what opens Stage 2, and opening Stage 2 runs the check (bug:D10).
 * A job that already has a result is not checked again.
 */
export async function approveAndOpenStage2(job: UC5Job, overrides: Overrides): Promise<ApproveOutcome> {
  let approved: UC5Job;
  try {
    approved = await uc5.approveSpec(job.id, overrides);
  } catch (e) {
    return { job, error: (e as Error).message, stage2Error: null };
  }
  if (!approved.spec_approved_by || approved.obsolescence) {
    return { job: approved, error: null, stage2Error: null };
  }
  const out = await checkObsolescence(approved);
  if (!out) return { job: approved, error: null, stage2Error: null };
  return { job: out.job, error: null, stage2Error: out.error };
}

/** The approved vendor list in force, or null if the status could not be read. Never blocks the page. */
export async function fetchApprovedListStatus(): Promise<ApprovedListStatus | null> {
  try {
    return await apiGet("/api/v1/vendors/approved-list/status");
  } catch {
    return null;
  }
}

/**
 * Whether the market search has results to show. A last-time-buy decision moves the job to stage
 * `ltb`, and a lifecycle check run again afterwards moves it back to `obsolescence`; in both the
 * stored candidates are still the ones a person is deciding on.
 */
export function marketSearched(job: UC5Job): boolean {
  return job.candidates != null || job.stage === "market" || job.stage === "ltb";
}

export type OutreachDraft = Schemas["OutreachDraft"];

/** The vendor enquiry, drafted from the approved specification. 409 before approval. */
export function draftOutreach(jobId: string): Promise<OutreachDraft> {
  return apiSend("/api/v1/uc5/jobs/{job_id}/outreach-draft", undefined, { path: { job_id: jobId } });
}

/** Puts the enquiry on the clipboard as one block, ready to paste into a mail client. */
export function copyDraft(
  draft: OutreachDraft,
  clipboard: Pick<Clipboard, "writeText"> = navigator.clipboard,
): Promise<void> {
  return clipboard.writeText(`Subject: ${draft.subject}\n\n${draft.body}`);
}

/**
 * Records that the signed-in person sent the enquiry. No body: the sender is the session, and a
 * second send is refused with 409 so the first record stands.
 */
export async function markOutreachSent(jobId: string): Promise<UC5Job> {
  const out = await apiSend("/api/v1/uc5/jobs/{job_id}/outreach-sent", undefined, { path: { job_id: jobId } });
  return out as unknown as UC5Job;
}

type LtbContract = Schemas["LtbDecisionRequest"];
/** The contract marks `option_index` required because it has a default; only `create` uses it. */
export type LtbRequest = Omit<LtbContract, "option_index"> & { option_index?: number };
export type LtbAction = LtbRequest["action"];

/** Posts one step of the last-time-buy decision and returns the job as the route left it. */
export async function ltbStep(jobId: string, body: LtbRequest): Promise<UC5Job> {
  const out = await apiSend("/api/v1/uc5/jobs/{job_id}/ltb-decision", body as LtbContract, { path: { job_id: jobId } });
  return out as unknown as UC5Job;
}

/** The stored options a last-time buy can be proposed on, by the index the route takes. */
export function lifeOfNeedOptions(job: UC5Job): number[] {
  return (job.candidates ?? []).flatMap((c, i) => (c.outcome === "life_of_need_buy" ? [i] : []));
}

/**
 * The steps the decision route accepts from each state (`_LTB_FROM` and `create` in uc5.py). A
 * proposal can be replaced; an approved or bridged one is closed or resolved first.
 */
export function ltbActions(decision: LtbDecision | null | undefined): LtbAction[] {
  switch (decision?.state ?? null) {
    case null: case "closed": return ["create"];
    case "proposed": return ["create", "approve", "close"];
    case "approved": return ["record_buy", "close"];
    case "bridged": return ["resolve", "close"];
    default: return [];
  }
}

/** The follow-on resolutions a bridge buy postpones (`FOLLOW_ON_RESOLUTIONS` in uc5.py). */
export const FOLLOW_ON = [
  "simple_substitute", "complex_substitute", "new_item_or_source", "next_higher_assembly_redesign", "system_redesign",
] as const;

/** The four life-of-need inputs, in the route's order, with the words on screen. */
export const LTB_INPUTS = [
  { name: "remaining_years", label: "Remaining years", hint: "years of life left on the asset" },
  { name: "rate", label: "Use per year", hint: "units consumed per year" },
  { name: "stock", label: "Stock on hand", hint: "units in the warehouse now" },
  { name: "moq", label: "Minimum order", hint: "smallest lot the vendor sells" },
] as const;

export type LtbInputName = (typeof LTB_INPUTS)[number]["name"];
export type LtbForm = Record<LtbInputName, { value: string; source: string }>;

function figure(text: string): number | null {
  const t = text.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * The `create` body. A blank or unreadable figure goes as missing, never as zero: the route then
 * answers `cannot_determine` and names it, which is the honest answer to a missing input.
 */
export function createBody(optionIndex: number, form: LtbForm): LtbRequest {
  const body: LtbRequest = { action: "create", option_index: optionIndex };
  for (const { name } of LTB_INPUTS) {
    const given = form[name];
    body[name] = { value: figure(given.value), source: given.source.trim() || null };
  }
  return body;
}

export interface ManualForm { manufacturer: string; model_number: string; part_number: string; description: string }

/** Starts a check typed by hand. Blank fields are left out rather than sent empty. */
export function startManual(form: ManualForm): Promise<UC5Job> {
  const t = (v: string) => v.trim() || undefined;
  return uc5.createManual({
    manufacturer: t(form.manufacturer),
    model_number: form.model_number.trim(),
    part_number: t(form.part_number),
    description: t(form.description),
  });
}

export type CaseView = Schemas["CaseView"];

export type CaseState =
  | { kind: "checking" }
  | { kind: "ready" }
  | { kind: "opened"; case: CaseView }
  | { kind: "off"; reason: string }
  | { kind: "error"; message: string }
  /** Refused because the register entry now holds another part than the one this check researched. */
  | { kind: "stale"; message: string };

/** The lifecycle findings a case opens on (`OPENS_ON` in cases.py). */
export const CASE_FINDINGS = ["obsolete", "announced"];

/**
 * Why a case cannot open on this job, or null if it can. The same refusals the route makes, so
 * the reason is on screen before the click rather than after it.
 */
export function caseBlock(job: UC5Job): string | null {
  const finding = job.obsolescence;
  if (!finding || !CASE_FINDINGS.includes(finding.status)) {
    return "A case opens on an obsolete or announced finding.";
  }
  if (!finding.evidence?.quote) return "The finding quotes no source, so a case would have nothing to cite.";
  if (!job.source_job_id) {
    return "This part was entered by hand, so it has no register entry for a case to link to.";
  }
  return null;
}

/** Opens a case on the job's finding. A 404 is the cases switch being off on this environment. */
export async function openCase(jobId: string): Promise<CaseState> {
  try {
    const opened = await apiSend("/api/v1/cases", { uc5_job_id: jobId });
    return { kind: "opened", case: opened };
  } catch (e) {
    const off = switchedOff(e, [404]);
    if (off !== null) return { kind: "off", reason: off };
    if (e instanceof ApiError && e.status === 409 && /now holds/.test(e.message)) {
      return { kind: "stale", message: e.message };
    }
    throw e;
  }
}

/**
 * A case already open on this job, if any, from the newest cases in scope. Also how the page
 * learns the cases switch is off before anyone clicks.
 */
export async function existingCase(jobId: string): Promise<CaseState> {
  try {
    const cases = await apiGet("/api/v1/cases", { query: { limit: 200 } });
    const found = cases.find((c) => c.uc5_job_id === jobId);
    return found ? { kind: "opened", case: found } : { kind: "ready" };
  } catch (e) {
    const off = switchedOff(e, [404]);
    if (off !== null) return { kind: "off", reason: off };
    return { kind: "error", message: (e as Error).message };
  }
}
